import type { RecordKind } from '../types';
import type { IssueSource, IssueSourceState } from './types';

const negativeClosure = [
  /^unresolved$/,
  /^not\s+(?:yet\s+)?(?:corrected|resolved|closed)$/,
  /^(?:pending\s+closure|closure\s+pending)$/,
  /^(?:open|active)(?:\s|$)/,
];
const commonClosure = [
  /^closed$/,
  /^case\s+closed$/,
  /^completed?$/,
  /^cancelled$/,
  /^canceled$/,
  /^expired$/,
  /^withdrawn$/,
  /^dismissed$/,
];
/**
 * SFFD stamps a close date on a violation when the inspector hands it off,
 * but a hearing referral or abatement order is the case escalating, not ending.
 */
const fireEscalation = [/^referred to hearing$/, /^order to abate$/, /^city attorney referral$/];
const explicitOpen = [
  /^open$/,
  /^active$/,
  /^pending$/,
  /^in\s+progress$/,
  /^issued$/,
  /^filed$/,
  /^received$/,
  /^under\s+review$/,
];

export function normalizeStatus(status: string) {
  return status
    .trim()
    .toLowerCase()
    .replace(/[_.-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function classifySourceState(input: {
  source?: IssueSource;
  recordKind?: RecordKind;
  status?: string | null;
  closedAt?: string | null;
}): IssueSourceState {
  const status = normalizeStatus(input.status ?? '');
  if (
    (input.source === 'fire-violations' || input.recordKind === 'fire-violation') &&
    fireEscalation.some((pattern) => pattern.test(status))
  )
    return 'open';
  const negative = negativeClosure.some((pattern) => pattern.test(status));
  const open = negative || explicitOpen.some((pattern) => pattern.test(status));
  if (open && validDate(input.closedAt)) return 'unknown';
  if (open) return 'open';
  const explicitClosure = [
    ...commonClosure,
    ...(input.source === 'sf311' || input.recordKind === '311' ? [/^resolved$/] : []),
    ...(input.source === 'fire-violations' || input.recordKind === 'fire-violation'
      ? [/^resolved$/, /^corrected$/, /^(?:case\s+)?abated$/]
      : []),
    ...(input.source === 'fire-inspections' || input.recordKind === 'fire-inspection'
      ? [/^final(?:ed)?$/]
      : []),
    ...(input.source === 'dbi' || !input.source ? [/^(?:case\s+)?abated$/] : []),
  ];
  if (explicitClosure.some((pattern) => pattern.test(status))) return 'closed';
  if (validDate(input.closedAt)) return 'closed';
  return 'unknown';
}

export function isExplicitlyClosed(
  status: string,
  closedAt?: string | null,
  source?: IssueSource,
  recordKind?: RecordKind,
) {
  return classifySourceState({ status, closedAt, source, recordKind }) === 'closed';
}

/** A fire inspection the department still expects to follow up on. */
export const FIRE_FOLLOW_UP = /\b(?:open|follow[\s-]?up|pending|scheduled)\b/i;
export const FIRE_NO_FOLLOW_UP = /\b(?:closed|no\s+follow[\s-]?up|complete[d]?)\b/i;

/**
 * A status that still reads as waiting on something, whatever end date the
 * source stamped. An archived record with such a status is still published.
 */
export function statusReadsOpen(status: string) {
  return FIRE_FOLLOW_UP.test(status) && !FIRE_NO_FOLLOW_UP.test(status);
}

/**
 * The department says this inspection still needs a follow-up visit
 * ("Open/Follow-Up Needed", "Follow-up required"); a scheduled or pending
 * visit is not yet that.
 */
export function fireInspectionNeedsFollowUp(status: string) {
  return /\b(?:open|follow[\s-]?up)\b/i.test(status) && !FIRE_NO_FOLLOW_UP.test(status);
}

function validDate(value?: string | null) {
  return Boolean(
    value && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) && Number.isFinite(Date.parse(value)),
  );
}
